import { toast } from "@/components/ui/use-toast";

interface AudioRecordingResult {
  audioBlob: Blob;
  audioDuration: number;
}

class AudioService {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private startTime: number = 0;
  private endTime: number = 0;

  /**
   * Starts recording audio from the user's microphone
   * @returns Promise that resolves when recording starts
   */
  startRecording = async (): Promise<void> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(stream);
      this.audioChunks = [];
      this.startTime = Date.now();

      this.mediaRecorder.addEventListener("dataavailable", (event) => {
        this.audioChunks.push(event.data);
      });

      this.mediaRecorder.start();
    } catch (error) {
      toast({
        title: "Recording Error",
        description: "Unable to access microphone. Please check permissions.",
        variant: "destructive",
      });
      throw new Error("Failed to start recording");
    }
  };

  /**
   * Stops recording audio
   * @returns Promise that resolves with the recorded audio blob and duration
   */
  stopRecording = (): Promise<AudioRecordingResult> => {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error("No active recording"));
        return;
      }

      this.mediaRecorder.addEventListener("stop", () => {
        this.endTime = Date.now();
        const audioDuration = (this.endTime - this.startTime) / 1000; // in seconds
        const audioBlob = new Blob(this.audioChunks, { type: "audio/wav" });
        
        // Stop all tracks to release the microphone
        const tracks = this.mediaRecorder?.stream.getTracks();
        tracks?.forEach(track => track.stop());
        
        resolve({ audioBlob, audioDuration });
      });

      this.mediaRecorder.stop();
    });
  };

  /**
   * Calculates words per minute based on transcript and audio duration
   * @param transcript The transcribed text
   * @param durationInSeconds Recording duration in seconds
   * @returns Estimated speech rate in words per minute
   */
  calculateSpeechRate = (transcript: string, durationInSeconds: number): number => {
    if (!transcript || durationInSeconds <= 0) {
      return 0;
    }
    
    // Count words (splitting by whitespace)
    const wordCount = transcript.trim().split(/\s+/).length;
    
    // Calculate words per minute
    const wordsPerMinute = Math.round((wordCount / durationInSeconds) * 60);
    
    return wordsPerMinute;
  };
}

export const audioService = new AudioService(); 